import { IPhotoAttachment } from "../types";

/**
 * A preview this browser made for a file it holds, never a URL from the server
 * — `URL.createObjectURL` is the only thing in the flow that produces one.
 */
const isLocalPreview = (url?: string): boolean =>
  Boolean(url?.startsWith("blob:"));

/**
 * Folds a draft response's photo list into what the client already knows,
 * keeping the local preview of any photo that still has one.
 *
 * The server names a *processed* variant (`…-thumbnail.webp`) that a background
 * job writes some time after the upload — the attach response says
 * `processed: false`, and the URL 403s until the job has run. So for the
 * seconds that matter, the only image that renders is the `blob:` URL the
 * browser made when the file was picked. Every other field is the server's:
 * the response decides which photos exist, and carries their captions.
 *
 * `undefined` incoming means the response said nothing about photos and the
 * client's list stands.
 */
export const mergePhotoPreviews = (
  known: IPhotoAttachment[] | undefined,
  incoming: IPhotoAttachment[] | undefined
): IPhotoAttachment[] | undefined => {
  if (!incoming) return known;
  if (!known?.length) return incoming;

  return incoming.map((photo) => {
    const local = known.find((candidate) => candidate.id === photo.id);

    if (!local || !isLocalPreview(local.thumb_url)) return photo;

    return {
      ...photo,
      thumb_url: local.thumb_url,
      normal_url: local.normal_url,
    };
  });
};
